import React, { useState } from 'react';
import { Check, Palette, Moon, Sun } from 'lucide-react';
import { MOTIFS, DEFAULT_MOTIF, MOTIF_BY_ID, SURFACE_MOTIFS, SURFACE_BY_ID, DEFAULT_SURFACE, pageVars } from '../../data/motifs';

// Centralized "Dizains" (appearance) panel shown in the owner dashboard editor.
// Controls the public page's SURFACE (background family) + ACCENT color in one
// place, with a live mini preview. Props:
//   surface, accent  -> current ids
//   template         -> decides the default accent swatch
//   onChangeSurface, onChangeAccent -> callbacks
// (Legacy: `value` / `onChange` still map to accent for older call sites.)
export default function ThemePicker({ surface, accent, value, template = 'reservations', onChangeSurface, onChangeAccent, onChange, families, title, subtitle }) {
  const activeAccent = accent || value || DEFAULT_MOTIF[template] || 'amber_warmth';
  const activeSurface = surface || DEFAULT_SURFACE;
  const changeAccent = onChangeAccent || onChange || (() => {});
  const changeSurface = onChangeSurface || (() => {});

  const allowedFamilies = families && families.length ? families : ['dark', 'light'];
  const activeFamily = (SURFACE_BY_ID[activeSurface] || SURFACE_BY_ID[DEFAULT_SURFACE]).family;
  const initFam = allowedFamilies.includes(activeFamily) ? activeFamily : allowedFamilies[0];
  const [fam, setFam] = useState(initFam);
  const showFamilyTabs = allowedFamilies.length > 1;
  const surfaces = SURFACE_MOTIFS.filter((s) => s.family === fam);
  const accentObj = MOTIF_BY_ID[activeAccent] || MOTIF_BY_ID.amber_warmth;

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid="dizains-panel">
      <h3 className="text-white font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
        <Palette className="w-4 h-4 text-[#D97736]" /> {title || 'Lapas dizains un motīvi'}
      </h3>
      <p className="text-white/45 text-xs mt-1 mb-5">
        {subtitle || 'Izvēlies publiskās lapas fonu un akcenta krāsu. Priekšskatījums atjaunojas uzreiz.'}
      </p>

      {/* --- Surface (fons) --- */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-white/70 text-xs font-bold uppercase tracking-wider">Fona motīvs</span>
          {showFamilyTabs && (
          <div className="inline-flex rounded-full bg-white/5 border border-white/10 p-0.5" data-testid="surface-family-tabs">
            <button type="button" onClick={() => setFam('dark')} data-testid="surface-family-dark"
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${fam === 'dark' ? 'bg-white/15 text-white' : 'text-white/50 hover:text-white/80'}`}>
              <Moon className="w-3 h-3" /> Tumšie
            </button>
            <button type="button" onClick={() => setFam('light')} data-testid="surface-family-light"
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${fam === 'light' ? 'bg-white/15 text-white' : 'text-white/50 hover:text-white/80'}`}>
              <Sun className="w-3 h-3" /> Gaišie
            </button>
          </div>
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          {surfaces.map((s) => {
            const active = s.id === activeSurface;
            return (
              <button key={s.id} type="button" onClick={() => changeSurface(s.id)}
                data-testid={`surface-swatch-${s.id}`} aria-pressed={active}
                className={`relative rounded-xl border-2 p-2.5 text-left transition-colors min-h-[72px] ${active ? 'border-white/80' : 'border-white/10 hover:border-white/30'}`}>
                <div className="rounded-lg h-9 w-full mb-2 flex items-center justify-end p-1.5 overflow-hidden"
                  style={{ background: s.vars['--bg-page'], border: `1px solid ${s.vars['--border-card']}` }}>
                  <span className="w-4 h-4 rounded-full ring-2 ring-black/20" style={{ background: accentObj.accent }} />
                </div>
                <span className="block text-white text-xs font-semibold truncate">{s.name_lv}</span>
                {active && <Check className="w-4 h-4 text-white absolute top-1.5 right-1.5" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* --- Accent --- */}
      <div className="mb-6">
        <span className="text-white/70 text-xs font-bold uppercase tracking-wider">Akcenta krāsa</span>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-3">
          {MOTIFS.map((m) => {
            const active = m.id === activeAccent;
            return (
              <button key={m.id} type="button" onClick={() => changeAccent(m.id)}
                data-testid={`motif-swatch-${m.id}`} aria-pressed={active}
                className={`relative flex items-center gap-2.5 p-2.5 rounded-xl border-2 text-left transition-colors min-h-[48px] ${active ? 'border-white/80 bg-white/[0.07]' : 'border-white/10 bg-white/[0.02] hover:border-white/30'}`}>
                <span className="w-7 h-7 rounded-full shrink-0 ring-2 ring-black/40" style={{ background: m.accent }} />
                <span className="block text-white text-xs font-semibold truncate pr-4">{m.name_lv}</span>
                {active && <Check className="w-4 h-4 text-white absolute top-1.5 right-1.5" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* --- Live preview --- */}
      <div>
        <span className="text-white/70 text-xs font-bold uppercase tracking-wider">Priekšskatījums</span>
        <div className="mt-3 rounded-xl overflow-hidden border border-white/10" data-testid="dizains-live-preview-box">
          <div style={{ ...pageVars(activeSurface, activeAccent, template), background: 'var(--bg-page)' }} className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: 'var(--accent)' }}>Latvija</p>
            <p className="text-lg font-bold leading-tight mt-0.5" style={{ color: 'var(--text-primary)', fontFamily: 'Outfit' }}>Tavs uzņēmums</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Sauklis parādās šeit</p>
            <div className="mt-3 rounded-lg p-3 shadow-sm flex items-center justify-between"
              style={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)' }}>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--accent)' }}>Dienas piedāvājums</p>
                <p className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>€6.90</p>
              </div>
              <span className="h-8 px-4 rounded-full text-xs font-bold inline-flex items-center"
                style={{ background: 'var(--accent)', color: 'var(--accent-contrast)' }}>Pasūtīt</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
